import { trpc } from "@/api/trpc";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/shared/components/checkbox";
import { FormError } from "@/shared/components/form-error";
import { TurnstileWidget, turnstileRequired } from "@/shared/components/turnstile-widget";
import { formatTerminDate } from "@/shared/format/termin-date";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "@tanstack/react-router";
import { formatDelkaDnu } from "@template/shared";
import { publicPrihlaskaSchema } from "@template/shared";
import { AlertCircle, Calendar, Clock, MapPin, Users } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import type { z } from "zod";

type FormValues = z.input<typeof publicPrihlaskaSchema>;

interface RegistrationFormProps {
  terminId: string;
}

const CURRENCY_FMT = new Intl.NumberFormat("cs-CZ", {
  style: "currency",
  currency: "CZK",
  maximumFractionDigits: 0,
});

function formatKc(value: string | null): string {
  if (!value) return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return CURRENCY_FMT.format(n);
}

export function RegistrationForm({ terminId }: RegistrationFormProps) {
  const navigate = useNavigate();
  const [turnstileToken, setTurnstileToken] = useState<string | undefined>(undefined);

  const terminQuery = trpc.hca.terminy.getByIdPublic.useQuery({ id: terminId }, { retry: false });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(publicPrihlaskaSchema),
    defaultValues: { terminId },
  });

  const submitMutation = trpc.hca.prihlasky.submitPublic.useMutation({
    onSuccess: (data) => {
      toast.success("Přihláška odeslána");
      navigate({ to: "/potvrzeni/$token", params: { token: data.token } });
    },
    onError: (err) => toast.error(err.message),
  });

  const onSubmit = handleSubmit((raw) => {
    const payload = {
      terminId,
      email: raw.email,
      jmeno: raw.jmeno,
      prijmeni: raw.prijmeni,
      telefon: raw.telefon,
      gdprConsent: true as const,
      ...(raw.firma && typeof raw.firma === "string" && raw.firma.trim().length > 0
        ? { firma: raw.firma.trim() }
        : {}),
      ...(raw.ico && typeof raw.ico === "string" && raw.ico.trim().length === 8
        ? { ico: raw.ico.trim() }
        : {}),
      ...(raw.dietaPozadavek &&
      typeof raw.dietaPozadavek === "string" &&
      raw.dietaPozadavek.trim().length > 0
        ? { dietaPozadavek: raw.dietaPozadavek.trim() }
        : {}),
      ...(raw.poznamka && typeof raw.poznamka === "string" && raw.poznamka.trim().length > 0
        ? { poznamka: raw.poznamka.trim() }
        : {}),
      ...(turnstileToken ? { turnstileToken } : {}),
    };
    submitMutation.mutate(payload);
  });

  if (terminQuery.isError) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-16 text-center" data-testid="hca-registrace-error">
        <AlertCircle className="mx-auto mb-4 h-12 w-12 text-destructive" aria-hidden="true" />
        <h1 className="mb-2 text-2xl font-bold">Termín není dostupný</h1>
        <p className="text-muted-foreground">{terminQuery.error.message}</p>
      </div>
    );
  }

  if (terminQuery.isPending) {
    return (
      <div className="mx-auto max-w-2xl space-y-6 px-4 py-16" data-testid="hca-registrace-skeleton">
        <Skeleton className="h-8 w-2/3" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  const termin = terminQuery.data;
  const volno = termin.kapacitaMax - termin.kapacitaObsazena;

  // Per-účastník cena se uplatní jen když je > 0; jinak katalogová cena za akci.
  // ("0.00" NENÍ NULL → kontrola > 0, ne `??`.) Musí odpovídat ceně, kterou
  // submitPublic ukládá na přihlášku a kterou faktura naúčtuje.
  const isPerUcastnik =
    !!termin.product.cenaPerUcastnik && Number(termin.product.cenaPerUcastnik) > 0;
  const cenaBezDph = isPerUcastnik ? termin.product.cenaPerUcastnik : termin.product.cenaKatalog;
  // Ceny v systému jsou bez DPH (faktura DPH připočítává). Na webu u kurzu
  // zobrazujeme VŽDY jen cenu bez DPH — přihláška je nezávazná, faktura vč. DPH
  // přijde až po potvrzení účasti.

  return (
    <div className="mx-auto max-w-2xl px-4 py-12" data-testid="hca-registrace-page">
      <header className="mb-6">
        <h1 className="text-3xl font-bold tracking-tight">Registrace na seminář</h1>
        <p className="mt-2 text-muted-foreground">
          Vyplňte údaje účastníka. Přihláška je nezávazná — po jejím odeslání se vám ozveme a
          potvrdíme účast.
        </p>
      </header>

      {/* Termin info */}
      <Card className="mb-6" data-testid="hca-registrace-termin-card">
        <CardHeader>
          <div className="text-xs font-semibold uppercase tracking-wider text-primary">
            {termin.product.kategorie}
          </div>
          <CardTitle className="text-2xl">{termin.product.nazev}</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 text-sm sm:grid-cols-2">
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            {formatTerminDate(termin.datumOd, termin.datumDo, { weekday: true })}
          </div>
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            {termin.misto}
          </div>
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            {formatDelkaDnu(termin.product.delkaDnu)}
          </div>
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            Volných míst: <strong>{volno}</strong>
          </div>
          <div className="sm:col-span-2 font-semibold">
            Cena: {formatKc(cenaBezDph)}
            <span className="ml-1 text-xs font-normal text-muted-foreground">
              {isPerUcastnik ? "za účastníka" : "za akci"} · bez DPH
            </span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-6">
          <FormError message={submitMutation.error?.message} testId="hca-registrace-form-error" />
          <form onSubmit={onSubmit} className="space-y-4" data-testid="hca-registrace-form">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="reg-jmeno">Jméno</Label>
                <Input
                  id="reg-jmeno"
                  autoFocus
                  aria-describedby={errors.jmeno ? "reg-jmeno-err" : undefined}
                  data-testid="hca-registrace-jmeno-input"
                  {...register("jmeno")}
                />
                {errors.jmeno ? (
                  <p
                    id="reg-jmeno-err"
                    className="text-sm text-destructive"
                    data-testid="hca-registrace-jmeno-error"
                  >
                    {errors.jmeno.message}
                  </p>
                ) : null}
              </div>
              <div className="space-y-2">
                <Label htmlFor="reg-prijmeni">Příjmení</Label>
                <Input
                  id="reg-prijmeni"
                  aria-describedby={errors.prijmeni ? "reg-prijmeni-err" : undefined}
                  data-testid="hca-registrace-prijmeni-input"
                  {...register("prijmeni")}
                />
                {errors.prijmeni ? (
                  <p
                    id="reg-prijmeni-err"
                    className="text-sm text-destructive"
                    data-testid="hca-registrace-prijmeni-error"
                  >
                    {errors.prijmeni.message}
                  </p>
                ) : null}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="reg-email">E-mail</Label>
              <Input
                id="reg-email"
                type="email"
                aria-describedby={errors.email ? "reg-email-err" : undefined}
                data-testid="hca-registrace-email-input"
                {...register("email")}
              />
              {errors.email ? (
                <p
                  id="reg-email-err"
                  className="text-sm text-destructive"
                  data-testid="hca-registrace-email-error"
                >
                  {errors.email.message}
                </p>
              ) : null}
            </div>

            <div className="space-y-2">
              <Label htmlFor="reg-telefon">Telefon</Label>
              <Input
                id="reg-telefon"
                type="tel"
                inputMode="tel"
                placeholder="+420 777 123 456"
                aria-describedby={errors.telefon ? "reg-telefon-err" : undefined}
                data-testid="hca-registrace-telefon-input"
                {...register("telefon")}
              />
              {errors.telefon ? (
                <p
                  id="reg-telefon-err"
                  className="text-sm text-destructive"
                  data-testid="hca-registrace-telefon-error"
                >
                  {errors.telefon.message}
                </p>
              ) : null}
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="reg-firma">Firma (volitelné)</Label>
                <Input
                  id="reg-firma"
                  data-testid="hca-registrace-firma-input"
                  {...register("firma")}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="reg-ico">IČO (volitelné)</Label>
                <Input
                  id="reg-ico"
                  inputMode="numeric"
                  maxLength={8}
                  placeholder="12345678"
                  aria-describedby={errors.ico ? "reg-ico-err" : undefined}
                  data-testid="hca-registrace-ico-input"
                  {...register("ico")}
                />
                {errors.ico ? (
                  <p
                    id="reg-ico-err"
                    className="text-sm text-destructive"
                    data-testid="hca-registrace-ico-error"
                  >
                    {errors.ico.message}
                  </p>
                ) : null}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="reg-poznamka">Poznámka pro organizátora (volitelné)</Label>
              <Textarea
                id="reg-poznamka"
                rows={3}
                maxLength={1000}
                placeholder="Například role účastníka, očekávání od semináře nebo návaznost na další lidi z firmy."
                aria-describedby={errors.poznamka ? "reg-poznamka-err" : undefined}
                data-testid="hca-registrace-poznamka-textarea"
                {...register("poznamka")}
              />
              {errors.poznamka ? (
                <p
                  id="reg-poznamka-err"
                  className="text-sm text-destructive"
                  data-testid="hca-registrace-poznamka-error"
                >
                  {errors.poznamka.message}
                </p>
              ) : null}
            </div>

            <div className="space-y-2">
              <Label htmlFor="reg-dieta">Dietní požadavky (volitelné)</Label>
              <Textarea
                id="reg-dieta"
                rows={2}
                maxLength={500}
                placeholder="Bezlepkově, vegetariánsky, bez ořechů…"
                data-testid="hca-registrace-dieta-textarea"
                {...register("dietaPozadavek")}
              />
            </div>

            <div className="space-y-2 rounded-lg border border-border p-4">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="reg-gdpr"
                  className="mt-0.5"
                  aria-labelledby="reg-gdpr-text"
                  aria-describedby={errors.gdprConsent ? "reg-gdpr-err" : undefined}
                  data-testid="hca-registrace-gdpr-checkbox"
                  {...register("gdprConsent")}
                />
                <p id="reg-gdpr-text" className="text-sm">
                  Odesílám nezávaznou přihlášku a souhlasím s{" "}
                  <a
                    href="/obchodni-podminky"
                    className="text-primary underline"
                    target="_blank"
                    rel="noreferrer"
                    data-testid="hca-registrace-vop-link"
                  >
                    obchodními podmínkami
                  </a>
                  . Beru na vědomí zpracování svých údajů pro účely vyřízení přihlášky, kontaktu a
                  komunikace o semináři dle{" "}
                  <a
                    href="/gdpr"
                    className="text-primary underline"
                    target="_blank"
                    rel="noreferrer"
                    data-testid="hca-registrace-gdpr-link"
                  >
                    zásad ochrany osobních údajů
                  </a>
                  .
                </p>
              </div>
              {errors.gdprConsent ? (
                <p
                  id="reg-gdpr-err"
                  className="text-sm text-destructive"
                  data-testid="hca-registrace-gdpr-error"
                >
                  Pro odeslání je nutné potvrdit souhlas s obchodními podmínkami.
                </p>
              ) : null}
            </div>

            <TurnstileWidget onToken={setTurnstileToken} testId="hca-registrace-turnstile" />

            {/* Informativní shrnutí přihlášky — cena je orientační, závaznou se
                stává až po potvrzení ze strany HCA Moravia. */}
            <div
              className="rounded-lg border border-border bg-muted/40 p-4 text-sm"
              data-testid="hca-registrace-recap"
            >
              <div className="mb-1 font-semibold text-foreground">Shrnutí přihlášky</div>
              <dl className="space-y-1">
                <div className="flex justify-between gap-3">
                  <dt className="text-muted-foreground">Seminář</dt>
                  <dd className="text-right font-medium">{termin.product.nazev}</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-muted-foreground">Termín</dt>
                  <dd className="text-right font-medium">
                    {formatTerminDate(termin.datumOd, termin.datumDo, { weekday: true })}
                  </dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-muted-foreground">Místo</dt>
                  <dd className="text-right font-medium">{termin.misto}</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-muted-foreground">Délka</dt>
                  <dd className="text-right font-medium">
                    {formatDelkaDnu(termin.product.delkaDnu)}
                  </dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-muted-foreground">Cena bez DPH</dt>
                  <dd className="text-right font-medium">{formatKc(cenaBezDph)}</dd>
                </div>
              </dl>
              <p className="pt-1 text-muted-foreground text-xs">
                Cena je orientační. Platba probíhá až na základě faktury po potvrzení účasti.
              </p>
            </div>

            <p
              className="text-center text-xs text-muted-foreground"
              data-testid="hca-registrace-payment-note"
            >
              Přihláška je nezávazná. Po jejím přijetí vás kontaktujeme a teprve potvrzením ze
              strany HCA Moravia vzniká smlouva a povinnost úhrady.
            </p>
            <Button
              type="submit"
              size="lg"
              className="w-full"
              disabled={submitMutation.isPending || (turnstileRequired && !turnstileToken)}
              data-testid="hca-registrace-submit"
            >
              {submitMutation.isPending ? "Odesílám…" : "Odeslat nezávaznou přihlášku"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
